// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION auth/session layer.
//
// Faithfully mirrors the CURRENT app's auth so the React app talks to the live
// auth worker IDENTICALLY. The legacy desktop wiring is IIFE/global JS; this is
// the typed equivalent of the auth bits scattered across:
//
//   • site/js/dashboard-wiring.js , TOKEN_KEY = 'cntrl_auth_token',
//                                     getToken(), authHeaders(json) (lines ~22,
//                                     149-156). Authorization: Bearer <token>;
//                                     Content-Type: application/json on writes.
//   • site/js/cntrl-token-shim.js , establishes 'cntrl_auth_token' as the
//                                     canonical key (exposed as
//                                     window.CNTRL_TOKEN_KEY) and migrates the
//                                     legacy 'bandsett_auth_token' into it.
//   • site/app.html (lines ~2882-2944), the X-Band-Id fetch interceptor:
//                                     reads ?bandId / window.acEmbedBandId and
//                                     attaches `X-Band-Id` to every auth-worker
//                                     request so the worker resolves the
//                                     impersonated (roster-viewed) band.
//   • site/js/ac-auth.js          , window.acAuth.headersFor(method): the same
//                                     Bearer + X-Band-Id contract, reading the
//                                     impersonation band from window.acEmbedBandId
//                                     / window.acTargetBandId.
//
// NOTE on the token key: ac-auth.js's header comment says 'auth_token', but the
// constant it actually uses is stale, every live desktop wiring (dashboard,
// connect, assets, messages, tasks, …) AND the token-shim use 'cntrl_auth_token'.
// We mirror the LIVE behaviour: 'cntrl_auth_token'.
// ─────────────────────────────────────────────────────────────────────────────

import { secureGet, secureSet, secureRemove, markLoggedOut } from './secureStore';

/**
 * Canonical localStorage key the desktop app stores the bearer token under.
 * Source of truth: site/js/cntrl-token-shim.js (CNTRL_KEY = 'cntrl_auth_token',
 * also published as window.CNTRL_TOKEN_KEY) and every site/js/*-wiring.js file.
 */
export const TOKEN_KEY = 'cntrl_auth_token';

/** Legacy key kept alive by the token-shim for backwards compatibility. */
export const LEGACY_TOKEN_KEY = 'bandsett_auth_token';

/**
 * HTTP methods that carry a JSON request body. For these the worker expects a
 * `Content-Type: application/json` header (mirrors ac-auth.js `_BODY_METHODS`
 * and dashboard-wiring.js authHeaders(json !== false)).
 */
const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Window globals the legacy app sets for roster-side impersonation context. */
interface AcImpersonationWindow {
  /** Set by app.html when loaded inside the roster shell iframe (?bandId=). */
  acEmbedBandId?: string | number;
  /** Fallback name some newer surfaces use (see ac-auth.js `_bandTargetId`). */
  acTargetBandId?: string | number;
  /** Published by cntrl-token-shim.js so callers can read the canonical key. */
  CNTRL_TOKEN_KEY?: string;
  /** Impersonated artist's band name, set by app.html (?bandName=). */
  acEmbedBandName?: string;
}

function win(): AcImpersonationWindow {
  return (typeof window !== 'undefined' ? window : {}) as AcImpersonationWindow;
}

/**
 * Read the bearer token from localStorage under the canonical key.
 * Mirrors dashboard-wiring.js getToken(), returns '' on any failure so callers
 * can branch on a falsy value exactly like the legacy code does.
 */
export function getToken(): string {
  // Routed through the secure store (Keychain/Preferences on native, localStorage
  // on web), mirrors legacy acSecure.getToken. Sync read off the primed cache.
  return secureGet(TOKEN_KEY);
}

/** Write the bearer token to the secure store (Keychain/Preferences + localStorage). */
export function setToken(token: string): void {
  void secureSet(TOKEN_KEY, token || '');
}

/** Clear the bearer token from every store + drop a logout marker (force-clears
 *  native tokens on the next boot, mirrors legacy clearToken + signOut marker).
 *  Returns the native-removal promise so sign-out can AWAIT it before reloading,
 *  otherwise on iOS the async Keychain/Preferences remove may not flush before the
 *  WebView reloads, and the boot reconcile restores the token (= logout no-ops). */
export function clearToken(): Promise<void> {
  markLoggedOut();
  /* persist 2026-07-24: cached screen snapshots are purged with the session —
     no business data outlives a logout on this machine. */
  try { void import('./dataCache').then((m) => m.purgePersistedCache()); } catch { /* best effort */ }
  return secureRemove(TOKEN_KEY);
}

/** True when a non-empty token is present. */
export function isAuthenticated(): boolean {
  return getToken().length > 0;
}

/**
 * The current impersonation / band context, the SAME source the band switcher
 * + app.html fetch interceptor read. Returns '' when not impersonating (artist
 * viewing their own band, where the worker resolves the band from the token).
 *
 * Mirrors ac-auth.js `_bandTargetId()`: window.acEmbedBandId, then
 * window.acTargetBandId, PLUS the standalone fallback the desktop band switcher
 * persists, `localStorage['ac:active_band_id']`. Without that last fallback, a
 * label/management account running the React app standalone (no roster iframe,
 * so no window.acEmbedBandId) sends no X-Band-Id and the worker resolves
 * `/api/data/*` to the CALLER's own (empty) blob instead of the artist they're
 * actively viewing, every screen comes back empty. The band switcher writes
 * `ac:active_band_id`, so reading it here makes standalone match the shell.
 */
export const ACTIVE_BAND_KEY = 'ac:active_band_id';
export function getCurrentBandId(): string {
  const w = win();
  if (w.acEmbedBandId !== undefined && w.acEmbedBandId !== null && w.acEmbedBandId !== '') {
    return String(w.acEmbedBandId);
  }
  if (w.acTargetBandId !== undefined && w.acTargetBandId !== null && w.acTargetBandId !== '') {
    return String(w.acTargetBandId);
  }
  try {
    const active = localStorage.getItem(ACTIVE_BAND_KEY);
    if (active) return String(active);
  } catch { /* ignore */ }
  return '';
}

/**
 * Build the auth headers for a worker request, faithfully reproducing the
 * legacy contract:
 *
 *   • Authorization: Bearer <token>                 (always)
 *   • Content-Type: application/json                (only on POST/PUT/PATCH/DELETE)
 *   • X-Band-Id: <currentBandId>                    (only under impersonation)
 *
 * This unifies dashboard-wiring.js `authHeaders(json)` (Bearer + conditional
 * Content-Type) with ac-auth.js `headersFor(method)` (the X-Band-Id attach) and
 * the app.html interceptor (which injects X-Band-Id on every auth-worker call).
 *
 * @param method HTTP method (defaults to GET). Drives the Content-Type header.
 */
export function authHeaders(method: string = 'GET'): Record<string, string> {
  const m = String(method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Authorization: 'Bearer ' + getToken(),
  };
  if (BODY_METHODS.has(m)) {
    headers['Content-Type'] = 'application/json';
  }
  const bandId = getCurrentBandId();
  if (bandId) {
    headers['X-Band-Id'] = bandId;
  }
  return headers;
}
